/**
 * accountant.service.ts — ledger, periods, taxes, reports, org profile,
 * currencies, recurring, compliance, migration, AI. Grouped (still one logical
 * "accountant suite" feature) and routed through the base handlers.
 */
import { read, write } from './_base';
import {
  Billing_Ledger_TrialBalance_Url,
  Billing_Ledger_Account_Url,
  Billing_Periods_Url,
  Billing_Period_Close_Url,
  Billing_Tax_Rates_Url,
  Billing_Tax_Groups_Url,
  Billing_Tax_Preview_Url,
  Billing_Report_TrialBalance_Url,
  Billing_Report_Pnl_Url,
  Billing_Report_BalanceSheet_Url,
  Billing_Report_CashFlow_Url,
  Billing_Report_Aging_Url,
  Billing_OrgProfile_Url,
  Billing_OrgProfile_Activate_Url,
  Billing_RegionPacks_Url,
  Billing_RegionPacks_States_Url,
  Billing_Currencies_Url,
  Billing_Currency_Url,
  Billing_ExchangeRates_Url,
  Billing_Migration_Batches_Url,
  Billing_Migration_Batch_Url,
  Billing_Migration_GoLive_Url,
  Billing_Gstr1_Url,
  Billing_Gstr3b_Url,
  Billing_Ai_Ask_Url,
} from '../../api/api.endpoints';
import {
  AccountingPeriod,
  CurrenciesResponse,
  OrgProfile,
  PeriodsResponse,
  TaxGroup,
  TaxPreviewPayload,
  TaxPreviewResult,
  TaxRate,
  TrialBalanceResponse,
} from '../../schema/billing/misc';

/* Ledger */
export const getTrialBalance = (as_of?: string) =>
  read<TrialBalanceResponse>(Billing_Ledger_TrialBalance_Url, 'Failed to load trial balance', as_of ? { as_of } : undefined);
export const getLedgerAccount = (id: string, params?: { from?: string; to?: string }) =>
  read(Billing_Ledger_Account_Url(id), 'Failed to load account', params);

/* Periods */
export const listPeriods = () =>
  read<PeriodsResponse>(Billing_Periods_Url, 'Failed to load periods').then((r) => r.periods ?? []);
export const closePeriod = (id: string) =>
  write<AccountingPeriod>('post', Billing_Period_Close_Url(id), { errorTitle: 'Close failed', successTitle: 'Period closed' });

/* Taxes */
export const listTaxRates = () =>
  read<{ tax_rates: TaxRate[] }>(Billing_Tax_Rates_Url, 'Failed to load tax rates').then((r) => r.tax_rates ?? []);
export const listTaxGroups = () =>
  read<{ tax_groups: TaxGroup[] }>(Billing_Tax_Groups_Url, 'Failed to load tax groups').then((r) => r.tax_groups ?? []);
export const createTaxRate = (body: { name: string; rate_percent: string; tax_kind: string }) =>
  write<{ id: string }>('post', Billing_Tax_Rates_Url, { body, errorTitle: 'Could not create tax rate', successTitle: 'Tax rate created' });
export const createTaxGroup = (body: { name: string; component_rate_ids?: string[]; is_exempt?: boolean }) =>
  write<{ id: string; name: string }>('post', Billing_Tax_Groups_Url, { body, errorTitle: 'Could not create tax', successTitle: 'Tax created' });
/**
 * The authoritative tax computation. The editor must NOT compute tax itself:
 * the server resolves jurisdiction, region-pack config and place-of-supply on
 * the same inputs `issue` will use, so a preview that disagrees with the issued
 * document is impossible by construction.
 */
export const previewTax = (body: TaxPreviewPayload) =>
  write<TaxPreviewResult>('post', Billing_Tax_Preview_Url, { body, errorTitle: 'Tax preview failed' });

/* Reports */
export const getAging = (side: 'AR' | 'AP' = 'AR', as_of?: string) =>
  read(Billing_Report_Aging_Url, 'Failed to load ageing', { side, as_of });
export const getPnl = (from: string, to: string) => read(Billing_Report_Pnl_Url, 'Failed to load P&L', { from, to });
export const getBalanceSheet = (as_of?: string) => read(Billing_Report_BalanceSheet_Url, 'Failed to load balance sheet', { as_of });
export const getCashFlow = (from: string, to: string) => read(Billing_Report_CashFlow_Url, 'Failed to load cash flow', { from, to });
export const getReportTrialBalance = (as_of?: string) => read(Billing_Report_TrialBalance_Url, 'Failed to load trial balance', { as_of });
export const getGstr1 = (period: string) => read(Billing_Gstr1_Url, 'Failed to load GSTR-1', { period });
export const getGstr3b = (period: string) => read(Billing_Gstr3b_Url, 'Failed to load GSTR-3B', { period });

/* Org profile / region packs / currencies */
export const getOrgProfile = () => read<OrgProfile>(Billing_OrgProfile_Url, 'Failed to load org profile');
export const activateOrgProfile = (body: Partial<OrgProfile>) =>
  write('post', Billing_OrgProfile_Activate_Url, { body, errorTitle: 'Activation failed', successTitle: 'Region pack activated' });
export const listRegionPacks = () => read(Billing_RegionPacks_Url, 'Failed to load region packs');
/** Place-of-supply states for the active region (India GST). */
export const listStates = () =>
  read<{ states: { code: string; name: string }[] }>(Billing_RegionPacks_States_Url, 'Failed to load states').then((r) => r.states ?? []);
export const listCurrencies = () =>
  read<CurrenciesResponse>(Billing_Currencies_Url, 'Failed to load currencies').then((r) => r.currencies ?? []);
export const getExchangeRate = (base: string, quote: string, date?: string) =>
  read(Billing_ExchangeRates_Url, 'Failed to load exchange rate', { base, quote, date }, true /* silent: a missing rate is normal */);
/** Enable (or disable) a currency for the org (ADMIN). */
export const enableCurrency = (code: string, is_enabled = true) =>
  write('put', Billing_Currency_Url(code), { body: { is_enabled }, errorTitle: 'Could not enable currency', successTitle: 'Currency enabled' });
/** Set a manual exchange rate base→quote for today. */
export const setExchangeRate = (base_currency: string, quote_currency: string, rate: string) =>
  write('post', Billing_ExchangeRates_Url, {
    body: { base_currency, quote_currency, rate, rate_date: new Date().toISOString().slice(0, 10) },
    errorTitle: 'Could not set exchange rate',
  });

/* Migration */
export const listMigrationBatches = () => read(Billing_Migration_Batches_Url, 'Failed to load migration batches');
export const getMigrationBatch = (id: string) => read(Billing_Migration_Batch_Url(id), 'Failed to load batch');
export const goLiveMigration = (id: string) =>
  write('post', Billing_Migration_GoLive_Url(id), { errorTitle: 'Go-Live failed', successTitle: 'Go-Live confirmed', successFallback: 'Opening balances are now locked.' });

/* AI conversational report */
export const askAi = (question: string) => write('post', Billing_Ai_Ask_Url, { body: { question }, errorTitle: 'AI request failed' });
